<template>
  <div>
    <form>
      <div class="dashboard-page-title">
        <div class="container-fluid">
          <h1>Profile Image</h1>

          <a @click="$router.go(-1)" class="btn btn-info" active-class="">
            <i class="fa fa-chevron-left"></i> Back
          </a>
        </div>
      </div>

      <div class="container-fluid">
        <div class="dashboard-block">
          <h2>Profile Picture</h2>

          <div class="panel panel-default">
            <div class="panel-body">
              <div class="row">
                <div class="col-lg-6">
                  <div class="form-horizontal">
                    <div :class="['form-group', errorClass('profile_image')]">
                      <label class="control-label col-sm-4">Upload Image</label>
                      <div class="col-sm-8">
                        <div class="file-upload-control">
                          <input
                            name="profile_image"
                            id="profile_image"
                            type="file"
                            @change="handleFileSelectionCallback"
                            @input="handleFileSelectionCallback">
                          <label for="profile_image" class="btn btn-default btn-block">Select Profile Picture</label>
                        </div>

                        <p class="help-block">For best results images should be 512px by 512px.</p>

                        <help-block field="profile_image"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <div style="width: 100%">
                    <img id="profile-image-crop" :src="userProfileImageOriginalUrl">
                  </div>
                </div>
              </div>
            </div>
            <div class="panel-footer">
              <ladda-button
                type="submit"
                class="btn btn-primary"
                :click="handleSaveProfileImageCallback"
                :disabled="userIsAdmin && !canEditAdmin">
                Save Profile Picture
              </ladda-button>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { pick } from 'lodash'
import { helpers } from 'mixins/users'
import { notify } from 'utils'
import { handleServerErrors } from 'utils/http'
import Cropper from 'cropperjs'
import inView from 'in-view'

export default {
  mixins: [helpers],

  computed: {
    ...mapState('user', ['user'])
  },

  data () {
    return {
      cropperInstance: null,
      imageContainer: null,
      file: '',
      cropPayload: {},
      images: []
    }
  },

  mounted () {
    this.imageContainer = document.getElementById('profile-image-crop')

    // Reset validator errors after the image is ready. This is necessary due to a weird issue
    // where resetting the errors before replacing the cropper image causes the image to not
    // be replaced when there are actually errors to reset.
    this.imageContainer.addEventListener('ready', () => {
      this.$validator.reset()
    })

    inView('#profile-image-crop')
      .once('enter', this.createCropperInstance)
  },

  methods: {
    ...mapActions('user', ['uploadUserProfileImage']),

    /**
     * Create the cropper instance.
     *
     * @return void
     */
    createCropperInstance () {
      this.cropperInstance = new Cropper(this.imageContainer, {
        aspectRatio: 1,
        zoomable: false,
        viewMode: 1,
        data: this.user.profile_image_crop_payload,
        crop: (event) => {
          this.cropPayload = pick(event.detail, ['x', 'y', 'width', 'height', 'scaleX', 'scaleY'])
        }
      })
    },

    /**
     * Handle file selection.
     *
     * This pulls the file from the input box and sets it on the cropper instance.
     *
     * @return void
     */
    handleFileSelectionCallback ({ target: { files } }) {
      this.errors.clear()

      if (files.length === 0) {
        this.file = ''

        return
      }

      this.file = files.item(0)

      if (!/^image\/\w+/.test(this.file.type)) {
        this.file = ''

        this.imageContainer.src = ''

        this.cropperInstance.destroy()

        this.$nextTick(() => {
          this.errors.add('profile_image', 'The file you have selected is an invalid image type.')
        })

        return
      }

      this.cropperInstance.replace(URL.createObjectURL(this.file))
    },

    /**
     * Handle saving the profile image to the server with the crop payload.
     *
     * @return Promise
     */
    handleSaveProfileImageCallback () {
      let payload = {
        id: this.user.id,
        file: this.file,
        cropPayload: this.cropPayload
      }

      return this.uploadUserProfileImage(payload)
        .then(() => {
          notify.success('Successfully saved profile image.')
          this.$validator.reset()
        })
        .catch((errors) => {
          if (errors.response) {
            handleServerErrors(errors, this.errors)
          } else {
            notify.error('Sorry something went wrong saving your image, please try again.')
            throw errors
          }
        })
    }
  }
}
</script>
